import { useCallback, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useAppStateProvider } from "../../../contextProviders/AppStateProvider";
import createTranslator from "../../../util/translationHelper";
import messages from "./translations/messages";
import getToken from "../../../util/tokenGenerator";
import { handleTokenRequest } from "../../../util/helper";
import helper from "./accountSettingsModalHelper";

function useAccountSettingsModal() {
  const { appState, updateAppStateLocal } = useAppStateProvider();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isRetry, setIsRetry] = useState(false);
  const { instance, inProgress, accounts } = useMsal();
  const getTokenHandler = () => getToken(instance, inProgress, accounts);
  const tokenhandler = async () => await handleTokenRequest(getTokenHandler);
  const translate = useMemo(() => createTranslator(messages), []);

  const isDisconnectOpen = appState?.disconnectModalOpen || false;
  const isCancelOpen = appState?.cancelSubscriptionModalOpen || false;
  const isReactivateOpen = appState?.reactivateModalOpen || false;
  const isOpen = isDisconnectOpen || isCancelOpen || isReactivateOpen;

  const mode = isDisconnectOpen
    ? helper.MODAL_MODE.DISCONNECT
    : isCancelOpen
      ? helper.MODAL_MODE.CANCEL
      : helper.MODAL_MODE.REACTIVATE;

  const subscription = appState?.qboSubscription;
  const lang = window?.localStorage?.getItem("lang") ?? "en";

  const title = useMemo(() => {
    if (hasError) return translate("errorTitle");
    switch (mode) {
      case helper.MODAL_MODE.DISCONNECT:
        return translate("disconnectTitle");
      case helper.MODAL_MODE.CANCEL:
        return translate("cancelTitle");
      case helper.MODAL_MODE.REACTIVATE:
        return translate("reactivateTitle");
      default:
        return "";
    }
  }, [mode, hasError, translate]);

  const confirmationMessage = useMemo(() => {
    if (hasError) return helper.getErrorBodyMessage(mode, translate);
    if (mode === helper.MODAL_MODE.DISCONNECT) {
      return translate("disconnectBody");
    }
    return subscription
      ? helper.getConfirmationMessage(mode, subscription, translate, lang)
      : "";
  }, [mode, hasError, subscription, translate, lang]);

  const confirmButtonText = useMemo(() => {
    if (hasError) return translate("errorRetry");
    switch (mode) {
      case helper.MODAL_MODE.DISCONNECT:
        return translate("disconnectConfirm");
      case helper.MODAL_MODE.CANCEL:
        return translate("cancelConfirm");
      case helper.MODAL_MODE.REACTIVATE:
        return translate("reactivateConfirm");
      default:
        return "";
    }
  }, [mode, hasError, translate]);

  const closeModal = useCallback(() => {
    setHasError(false);
    setIsRetry(false);
    updateAppStateLocal({
      disconnectModalOpen: false,
      cancelSubscriptionModalOpen: false,
      reactivateModalOpen: false,
      accountSettingsSidebarOpen: true,
    });
  }, [updateAppStateLocal]);

  const closeWithGenericError = useCallback(() => {
    setHasError(false);
    setIsRetry(false);
    updateAppStateLocal({
      disconnectModalOpen: false,
      cancelSubscriptionModalOpen: false,
      reactivateModalOpen: false,
      accountSettingsSidebarOpen: false,
      disconnectErrorStatusModal: true,
    });
  }, [updateAppStateLocal]);

  const onApiError = useCallback(() => {
    if (isRetry) {
      closeWithGenericError();
    } else {
      setHasError(true);
      setIsRetry(true);
    }
  }, [isRetry, closeWithGenericError]);

  const handleDisconnect = useCallback(async () => {
    setIsLoading(true);
    const partnerAccountId = appState?.qboConnection?.partner_account_id;

    try {
      const response = await helper.submitDisconnect(partnerAccountId, tokenhandler);

      setIsLoading(false);
      if (response?.status === 200) {
        setHasError(false);
        setIsRetry(false);
        updateAppStateLocal({
          disconnectModalOpen: false,
          disconnectedStatusModal: true,
          refreshConnection: true,
        });
      } else {
        onApiError();
      }
    } catch {
      setIsLoading(false);
      onApiError();
    }
  }, [appState?.qboConnection, tokenhandler, updateAppStateLocal, onApiError]);

  const handleCancelSubscription = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await helper.submitCancelSubscription(tokenhandler);

      setIsLoading(false);
      if (response?.status === 200) {
        setHasError(false);
        setIsRetry(false);
        updateAppStateLocal({
          cancelSubscriptionModalOpen: false,
          refreshConnection: true,
          cancelSubscriptionSuccess: true,
        });
      } else {
        onApiError();
      }
    } catch {
      setIsLoading(false);
      onApiError();
    }
  }, [tokenhandler, updateAppStateLocal, onApiError]);

  const handleReactivate = useCallback(async () => {
    setIsLoading(true);

    const billingFrequency = subscription?.billing_frequency;
    if (!billingFrequency) {
      setIsLoading(false);
      onApiError();
      return;
    }

    try {
      const response = await helper.submitReactivate(billingFrequency, tokenhandler);

      setIsLoading(false);
      if (response?.status === 200) {
        setHasError(false);
        setIsRetry(false);
        updateAppStateLocal({
          reactivateModalOpen: false,
          refreshConnection: true,
          reactivateSuccess: true,
        });
      } else {
        onApiError();
      }
    } catch {
      setIsLoading(false);
      onApiError();
    }
  }, [subscription?.billing_frequency, tokenhandler, updateAppStateLocal, onApiError]);

  const handleConfirm = useCallback(async () => {
    switch (mode) {
      case helper.MODAL_MODE.DISCONNECT:
        return handleDisconnect();
      case helper.MODAL_MODE.CANCEL:
        return handleCancelSubscription();
      case helper.MODAL_MODE.REACTIVATE:
        return handleReactivate();
      default:
        break;
    }
  }, [mode, handleDisconnect, handleCancelSubscription, handleReactivate]);

  const isDestructive = !hasError && (mode === helper.MODAL_MODE.DISCONNECT || mode === helper.MODAL_MODE.CANCEL);

  return {
    isOpen,
    isLoading,
    mode,
    hasError,
    title,
    confirmationMessage,
    confirmButtonText,
    isDestructive,
    closeModal,
    handleConfirm,
  };
}

const Hook = {
  useAccountSettingsModal,
};

export default Hook;
